import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import "../i18n"; // Import i18n setup
import '../locales/Slogan.css';
import { useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import '../styles/CorporatePhilosophy.css';

const CorporatePhilosophy = () => {
    const { t } = useTranslation();

    const location = useLocation();

    const missionRef = useRef(null);
    const visionRef = useRef(null);

    useEffect(() => {
        const yOffset = -100; // Adjust if you have a fixed header
        let targetRef = null;

        if (location.hash === '#mission') targetRef = missionRef;
        else if (location.hash === '#vision') targetRef = visionRef;

        if (targetRef?.current) {
            const y =
                targetRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }, [location]);

    // Add scroll animation hooks
    const { scrollYProgress } = useScroll();
    const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

    // Enhanced animation variants
    const fadeInScale = {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const slideIn = {
        hidden: { opacity: 0, x: -100 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    return (
        <section className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
            {/* Enhanced Hero Section */}
            <motion.div 
                className="relative min-h-[300px] lg:h-screen flex items-center justify-center"
                style={{ opacity, scale }}
            >
                <motion.div 
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5 }}
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage: "url('/images/corporate_philosophy/CorporatePhilosophyBanner.gif')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.3)'
                    }}
                />
                <motion.div 
                    className="relative z-10 text-center px-4 space-y-6"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                >
                    <motion.h1 
                        className="text-4xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600"
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        {t("corporatePhilosophy:corporatePhilosophy.title")}
                    </motion.h1>
                </motion.div>
            </motion.div>

            {/* Enhanced Commitment Section */}
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative py-16 md:py-32 skew-y-3 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 backdrop-blur-sm"
            >
                <div className="container mx-auto px-4 md:px-6 -skew-y-3">
                    <motion.div 
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center"
                        variants={{
                            hidden: {},
                            visible: {
                                transition: {
                                    staggerChildren: 0.2
                                }
                            }
                        }}
                    >
                        <motion.div
                            variants={slideIn}
                            className="space-y-6"
                        >
                            <motion.h2 
                                className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent"
                                whileHover={{ scale: 1.02 }}
                            >
                                {t("corporatePhilosophy:corporatePhilosophy:commitment.title")}
                            </motion.h2>
                            <motion.p 
                                className="text-base md:text-lg text-gray-300 leading-relaxed"
                                variants={fadeInScale}
                            >
                                {t("corporatePhilosophy:corporatePhilosophy:commitment.description")}
                            </motion.p>
                        </motion.div>
                        <motion.div 
                            className="relative group"
                            variants={fadeInScale}
                        >
                            <motion.div 
                                className="absolute -inset-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl opacity-30 blur-xl transition duration-1000"
                                whileHover={{ opacity: 0.5, scale: 1.05 }}
                            />
                            <motion.img
                                src="/images/corporate_philosophy/CorporatePhilosophy.svg"
                                alt="Corporate Philosophy visualization"
                                className="relative rounded-xl shadow-2xl w-full h-auto"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Vision & Mission Cards with enhanced animations */}
            <div className="w-full py-16 md:py-32">
                <motion.div 
                    className="max-w-[90vw] mx-auto space-y-24" // Increased width and vertical spacing
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={{
                        hidden: {},
                        visible: {
                            transition: {
                                staggerChildren: 0.3
                            }
                        }
                    }}
                >
                    {/* Vision Card */}
                    <motion.div
                        variants={slideIn}
                        whileHover={{ scale: 1.01 }}
                        className="rounded-3xl bg-gradient-to-br from-purple-900/50 to-indigo-900/50 p-12 backdrop-blur-xl 
                                 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500 
                                 shadow-xl hover:shadow-2xl transform-gpu overflow-hidden"
                    >
                        <div id="vision" ref={visionRef} className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                className="relative z-10"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="text-6xl font-bold mb-12 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                    {t("corporatePhilosophy:corporatePhilosophy:vision.title")}
                                </h2>
                                <p className="text-xl text-gray-300 leading-relaxed tracking-wide">
                                    {t("corporatePhilosophy:corporatePhilosophy:vision.description")}
                                </p>
                            </motion.div>
                            <motion.div 
                                className="relative" // Increased height
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <motion.div 
                                    className="absolute -inset-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-30 blur-2xl"
                                    animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.3, 0.5, 0.3]
                                    }}
                                    transition={{ 
                                        duration: 8,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                    }}
                                />
                                <motion.img
                                    src="/images/corporate_philosophy/Vision.svg"
                                    alt="Vision visualization"
                                    className="relative w-full object-contain rounded-2xl"
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                />
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Mission Card */}
                    <motion.div
                        variants={slideIn}
                        whileHover={{ scale: 1.01 }}
                        className="rounded-3xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-12 backdrop-blur-xl 
                                 border border-indigo-500/20 hover:border-indigo-500/40 transition-all duration-500 
                                 shadow-xl hover:shadow-2xl transform-gpu overflow-hidden"
                    >
                        <div id="mission" ref={missionRef} className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div 
                                className="relative order-2 lg:order-1" // Increased height
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <motion.div 
                                    className="absolute -inset-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl opacity-30 blur-2xl"
                                    animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.3, 0.5, 0.3]
                                    }}
                                    transition={{ 
                                        duration: 8,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                    }}
                                />
                                <motion.img
                                    src="/images/corporate_philosophy/Mission.svg"
                                    alt="Mission visualization"
                                    className="relative w-full object-contain rounded-2xl"
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                />
                            </motion.div>
                            <motion.div
                                className="relative z-10 order-1 lg:order-2"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="text-6xl font-bold mb-12 bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
                                    {t("corporatePhilosophy:corporatePhilosophy:mission.title")}
                                </h2>
                                <p className="text-xl text-gray-300 leading-relaxed tracking-wide">
                                    {t("corporatePhilosophy:corporatePhilosophy:mission.description")}
                                </p>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Core Values Section - Enhanced Design */}
            <div className="relative py-32 bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
                <div className="container mx-auto px-6">
                    {/* Animated Title Section */}
                    <motion.div 
                        className="text-center mb-24"
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h2 
                            className="text-7xl font-bold bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 200 }}
                        >
                            {t("corporatePhilosophy:corporatePhilosophy:coreValues.title")}
                        </motion.h2>
                        <motion.div 
                            className="h-1 w-32 mx-auto mt-6 bg-gradient-to-r from-purple-500 to-pink-500"
                            initial={{ width: 0 }}
                            whileInView={{ width: 128 }}
                            transition={{ duration: 1 }}
                        />
                    </motion.div>

                    {/* Values Grid with Central Image */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
                        {/* Left Column Values */}
                        <div className="space-y-12">
                            <motion.div 
                                className="value-card"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm 
                                              border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500
                                              transform hover:scale-105 hover:shadow-2xl">
                                    <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.innovation.title")}
                                    </h3>
                                    <p className="text-lg text-gray-300 leading-relaxed">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.innovation.description")}
                                    </p>
                                </div>
                            </motion.div>
                            <motion.div 
                                className="value-card"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm 
                                              border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500
                                              transform hover:scale-105 hover:shadow-2xl">
                                    <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.excellence.title")}
                                    </h3>
                                    <p className="text-lg text-gray-300 leading-relaxed">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.excellence.description")}
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Center Column - Values Image */}
                        <motion.div 
                            className="relative"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1 }}
                        >
                            <motion.div 
                                className="absolute -inset-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full opacity-30 blur-3xl"
                                animate={{ 
                                    scale: [1, 1.2, 1],
                                    opacity: [0.3, 0.5, 0.3],
                                    rotate: 360
                                }}
                                transition={{ 
                                    duration: 20,
                                    repeat: Infinity,
                                    repeatType: "loop"
                                }}
                            />
                            <motion.img
                                src="/images/corporate_philosophy/Values.svg"
                                alt="Core Values visualization"
                                className="relative w-full object-contain"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                        </motion.div>

                        {/* Right Column Values */}
                        <div className="space-y-12">
                            <motion.div 
                                className="value-card"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm 
                                              border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500
                                              transform hover:scale-105 hover:shadow-2xl">
                                    <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.socialImpact.title")}
                                    </h3>
                                    <p className="text-lg text-gray-300 leading-relaxed">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.socialImpact.description")}
                                    </p>
                                </div>
                            </motion.div>
                            <motion.div 
                                className="value-card"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/30 backdrop-blur-sm 
                                              border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500
                                              transform hover:scale-105 hover:shadow-2xl">
                                    <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.partnership.title")}
                                    </h3>
                                    <p className="text-lg text-gray-300 leading-relaxed">
                                        {t("corporatePhilosophy:corporatePhilosophy:coreValues.partnership.description")}
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Business Ethics Image with Hyperlink */}
            <div className="w-full p-4 mt-8 relative">
                <a
                    href="/businessethics"
                    className="hover:opacity-80 transition-opacity duration-300 block"
                >
                    <img
                        src="/images/corporate_philosophy/BusinessEthics.svg"
                        alt="Business Ethics"
                        className="w-full h-auto"
                    />
                    <div className="absolute inset-0 flex flex-col justify-center items-center">
                        <div className="px-8 py-4 bg-black bg-opacity-50 rounded-lg">
                            <h2 className="text-6xl font-bold text-center text-white">
                                {t("corporatePhilosophy:corporatePhilosophy:businessEthics.title")}
                            </h2>
                            <p className="mt-2 text-center text-base text-white">
                                {t("corporatePhilosophy:corporatePhilosophy:businessEthics.read_more")}
                            </p>
                        </div>
                    </div>
                </a>
            </div>
        </section>
    );
};

export default CorporatePhilosophy;